import React, { useCallback, useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import { geocoderApiOptions, MAPBOX_ACCESS_TOKEN } from '../../services/DefaultValuesUtils';
import HookFormTextField from '../hook-form/HookFormTextField';
import HookFormUtils from '../hook-form/HookFormUtils';

const AddressAutocomplete = ({ name, control, errors, prefix, label, placeholder, handleRetrieve }) => {
  let _schema = HookFormUtils.getSchemaName(name, prefix);
  const {
    field: { onChange, onBlur, value, ref },
  } = useController({ name: _schema, control });

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [sessionToken, setSessionToken] = useState('');

  const fetchSuggestions = useCallback(async (query) => {
    if (!sessionToken) return;

    const baseUrl = 'https://api.mapbox.com/autofill/v1/suggest';
    const params = new URLSearchParams({
      types: 'address',
      streets: 'true',
      language: 'en',
      country: 'us',
      proximity: 'ip',
      access_token: MAPBOX_ACCESS_TOKEN,
      session_token: sessionToken,
      ...geocoderApiOptions,
    });

    try {
      const response = await fetch(`${baseUrl}/${encodeURIComponent(query)}?${params.toString()}`);
      const data = await response.json();
      setSuggestions(data?.suggestions || []);
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
    }
  }, [sessionToken]);

  useEffect(() => {
    const token = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    setSessionToken(token);
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }

    const delay = setTimeout(() => {
      fetchSuggestions(query);
    }, 500); // debounce delay in ms

    return () => clearTimeout(delay); // cleanup on next keystroke
  }, [query, fetchSuggestions]);


  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setQuery(newValue);
    onChange(newValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      setActiveSuggestionIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      setActiveSuggestionIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeSuggestionIndex >= 0) {
      e.preventDefault();
      handleSuggestionSelect(suggestions[activeSuggestionIndex]);
    }
  };

  const handleSuggestionSelect = (suggestion) => {
    const full_address = `${suggestion.address_line1}, ${suggestion.place}, ${suggestion.region}, ${suggestion.postcode}`;
    handleRetrieve({...suggestion, full_address});

    const mapping = {
      'address_line1': 'address_line1',
      'address_city': 'place',
      'address_state': 'region',
      'address_zip': 'postcode'
    }

    const address = suggestion[mapping[name]] || full_address;
    onChange(address);
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
    console.log('Selected:', suggestion);
  };

  return (
    <div style={{ position: 'relative' }}>
      <HookFormTextField
        name={_schema}
        label={label}
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={onBlur}
        inputRef={ref}
        fullWidth
        errors={errors}
        schema={_schema}
        control={control}
      />
      {suggestions.length > 0 && (
        <ul
          style={{
            position: 'absolute',
            zIndex: 1300,
            backgroundColor: '#fff',
            listStyle: 'none',
            margin: 0,
            padding: 0,
            border: '1px solid #ccc',
            borderRadius: 4,
            width: '100%',
            maxHeight: 250,
            overflowY: 'auto',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          {suggestions.map((suggestion, idx) => (
            <li
              key={suggestion.id}
              onMouseDown={() => handleSuggestionSelect(suggestion)}
              onMouseEnter={() => setActiveSuggestionIndex(idx)}
              style={{
                padding: '0.5rem 0.75rem',
                borderBottom: idx !== suggestions.length - 1 ? '1px solid #e0e0e0' : 'none',
                backgroundColor: idx === activeSuggestionIndex ? '#f5f5f5' : '#fff',
                cursor: 'pointer',
                fontSize: '0.95rem',
                transition: 'background-color 0.2s ease',
              }}
            >
              <strong
                style={{
                  fontSize: '1rem',
                  color: '#333',
                  fontWeight: 600,
                }}
              >
                {suggestion.address_line1}
              </strong>

              <br/>
              <span
                style={{
                  fontSize: '0.875rem',
                  color: '#666',
                  lineHeight: '1.4',
                }}
              >
                {suggestion.description && `${suggestion.description} `}
              </span>
            </li>
          ))}
        </ul>      
      )}
    </div>
  );
};

export default AddressAutocomplete;
